import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import * as XLSX from 'xlsx';

// ── Country themes ──
const COUNTRY_THEMES = {
  japan:     { flag: '\u{1F1EF}\u{1F1F5}', label: 'Japan',          accent: '#c8102e', accent2: '#d4a847' },
  france:    { flag: '\u{1F1EB}\u{1F1F7}', label: 'France',         accent: '#0055a4', accent2: '#ef4135' },
  italy:     { flag: '\u{1F1EE}\u{1F1F9}', label: 'Italy',          accent: '#009246', accent2: '#d4a847' },
  uk:        { flag: '\u{1F1EC}\u{1F1E7}', label: 'United Kingdom', accent: '#012169', accent2: '#c8102e' },
  australia: { flag: '\u{1F1E6}\u{1F1FA}', label: 'Australia',      accent: '#00843d', accent2: '#FFCD00' },
  usa:       { flag: '\u{1F1FA}\u{1F1F8}', label: 'USA',            accent: '#3c3b6e', accent2: '#b22234' },
};

// ── Weighted country detection signals [keyword, country, weight] ──
// weight 3 = definitive, 2 = strong city/region, 1 = weak/shared
// All matched with \b word boundaries — 'uk' won't fire inside 'tsukiji', 'osaka' etc.
const COUNTRY_SIGNALS = [
  // Japan — definitive
  ['japan',      'japan', 3], ['japanese',   'japan', 3], ['jpy',        'japan', 3],
  ['shinkansen', 'japan', 3], ['ryokan',     'japan', 3], ['onsen',      'japan', 3],
  ['suica',      'japan', 3], ['tatami',     'japan', 3], ['kimono',     'japan', 3],
  ['yen',        'japan', 2], ['shrine',     'japan', 2], ['temple',     'japan', 1],
  // Japan — cities & neighbourhoods
  ['tokyo',      'japan', 2], ['kyoto',      'japan', 2], ['osaka',      'japan', 2],
  ['nagoya',     'japan', 2], ['hiroshima',  'japan', 2], ['nara',       'japan', 2],
  ['yokohama',   'japan', 2], ['sapporo',    'japan', 2], ['fukuoka',    'japan', 2],
  ['okinawa',    'japan', 2], ['kobe',       'japan', 2], ['sendai',     'japan', 2],
  ['shinjuku',   'japan', 2], ['shibuya',    'japan', 2], ['akihabara',  'japan', 2],
  ['asakusa',    'japan', 2], ['ginza',      'japan', 2], ['harajuku',   'japan', 2],
  ['nikko',      'japan', 2], ['hakone',     'japan', 2], ['suzuka',     'japan', 2],
  ['fushimi',    'japan', 2], ['arashiyama', 'japan', 2], ['gion',       'japan', 2],
  // UK — definitive (\b ensures 'uk' only matches as an isolated word)
  ['united kingdom','uk', 3], ['britain',    'uk', 3], ['british',      'uk', 3],
  ['england',    'uk', 3],    ['scotland',   'uk', 3], ['wales',        'uk', 2],
  ['gbp',        'uk', 3],    ['sterling',   'uk', 3],
  ['london',     'uk', 2],    ['manchester', 'uk', 2], ['edinburgh',    'uk', 2],
  ['birmingham', 'uk', 2],    ['liverpool',  'uk', 2], ['glasgow',      'uk', 2],
  ['oxford',     'uk', 2],    ['cambridge',  'uk', 2],
  // France
  ['france',     'france', 3], ['french',   'france', 3],
  ['paris',      'france', 2], ['lyon',     'france', 2], ['marseille',  'france', 2],
  ['bordeaux',   'france', 2], ['nice',     'france', 2], ['versailles', 'france', 2],
  // Italy
  ['italy',      'italy', 3], ['italian',   'italy', 3],
  ['rome',       'italy', 2], ['milan',     'italy', 2], ['venice',      'italy', 2],
  ['florence',   'italy', 2], ['naples',    'italy', 2], ['sicily',      'italy', 2],
  // Australia
  ['australia',  'australia', 3], ['australian','australia', 3], ['aud',  'australia', 3],
  ['sydney',     'australia', 2], ['melbourne', 'australia', 2], ['brisbane','australia',2],
  ['perth',      'australia', 2], ['adelaide',  'australia', 2], ['cairns', 'australia', 2],
  // USA
  ['usa',        'usa', 3], ['united states','usa', 3], ['american',    'usa', 2],
  ['usd',        'usa', 3], ['new york',     'usa', 2], ['los angeles', 'usa', 2],
  ['chicago',    'usa', 2], ['las vegas',    'usa', 2], ['san francisco','usa', 2],
];

const detectCountry = (text) => {
  const t = text.toLowerCase();
  const scores = {};
  for (const [keyword, country, weight] of COUNTRY_SIGNALS) {
    const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\\s\+/g, '\\s+');
    const pattern = new RegExp('\\b' + escaped + '\\b', 'i');
    if (pattern.test(t)) scores[country] = (scores[country] || 0) + weight;
  }
  if (Object.keys(scores).length === 0) return 'japan';
  return Object.entries(scores).sort((a, b) => b[1] - a[1])[0][0];
};

const EXPENSE_CATS = ['Transport', 'Hotel', 'Food', 'Attractions', 'General'];

const STATIC_SUGGESTIONS = {
  tokyo:   ['Senso-ji Temple (Asakusa)', 'Shibuya Crossing at night', 'Tsukiji Outer Market', 'TeamLab Planets', 'Meiji Shrine'],
  kyoto:   ["Fushimi Inari torii gates at dawn", 'Arashiyama bamboo grove', 'Nijo Castle', "Philosopher's Path", 'Nishiki Market'],
  nagoya:  ['Nagoya Castle', 'Toyota Commemorative Museum', 'Osu Shopping District', 'Atsuta Shrine', 'Nagoya TV Tower'],
  default: ['Local morning market', 'Historical city centre walk', 'Regional cuisine dinner', 'Museum visit', 'Local park or garden'],
};

const API = process.env.REACT_APP_API_URL;

function mapCatToBackend(cat) {
  return { Transport: 'Travel', Hotel: 'Accommodation', Food: 'Food', Attractions: 'General', General: 'General' }[cat] || 'General';
}
function mapCatFromBackend(cat) {
  return { Travel: 'Transport', Accommodation: 'Hotel', Food: 'Food', General: 'General', Equipment: 'General', 'Pre-Hike': 'General' }[cat] || 'General';
}

// ── Restore itinerary from localStorage (dateObj must be reconstructed) ──
function restoreItinerary() {
  try {
    const raw = localStorage.getItem('tc_itinerary');
    if (!raw) return [];
    return JSON.parse(raw).map(d => ({ ...d, dateObj: new Date(d.date) }));
  } catch { return []; }
}

// ════════════════════════════════════════════════
export default function TravelCompanion() {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');

  const [itinerary, setItinerary]     = useState(() => restoreItinerary());
  const [currentDay, setCurrentDay]   = useState(null);
  const [expenses, setExpenses]       = useState({});
  const [suggestions, setSuggestions] = useState({});
  const [hikeId, setHikeId]           = useState(localStorage.getItem('tc_hikeId') || null);
  const [theme, setTheme]             = useState(() => {
    const saved = localStorage.getItem('tc_theme');
    return saved ? (COUNTRY_THEMES[saved] || COUNTRY_THEMES.japan) : COUNTRY_THEMES.japan;
  });
  const [dragOver, setDragOver]       = useState(false);

  // Expense form state
  const [expCat, setExpCat]       = useState('General');
  const [expDesc, setExpDesc]     = useState('');
  const [expAmount, setExpAmount] = useState('');
  const [expSaving, setExpSaving] = useState(false);
  const [expStatus, setExpStatus] = useState('');

  const [aiLoading, setAiLoading]   = useState(false);
  const [syncLoading, setSyncLoading] = useState(!!user); // true on load if logged in — waiting for backend
  const [sugLoading, setSugLoading] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('tc_darkmode') !== 'false');
  const [editingPlan, setEditingPlan] = useState(false);
  const [planDraft, setPlanDraft] = useState('');
  const [tripId, setTripId] = useState(() => localStorage.getItem('tc_tripId') || null);
  const [trips, setTrips] = useState([]);
  const [showTripManager, setShowTripManager] = useState(false);
  const [renamingTrip, setRenamingTrip] = useState(null); // tripId being renamed
  const [renameValue, setRenameValue] = useState('');
  const [weather, setWeather] = useState({}); // keyed by "location::date"
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [fxRates, setFxRates] = useState(null);
  const [fxFrom, setFxFrom] = useState('JPY');
  const [fxTo, setFxTo] = useState('AUD');
  const [fxAmount, setFxAmount] = useState('');
  const [fxResult, setFxResult] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);

  // (Anthropic API key now lives on the backend only — calls go through /api/travel/ai/*)

  // On mount: restore currentDay from saved itinerary
  useEffect(() => {
    const saved = restoreItinerary();
    if (saved.length > 0 && !currentDay) {
      const today = new Date().toISOString().split('T')[0];
      const todayEntry = saved.find(d => d.date === today);
      const first = todayEntry || saved.find(d => d.activities || d.hotel) || saved[0];
      setCurrentDay(first);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // ── Ensure "Travel Companion" hike exists in BootExcursie ──
  const ensureHike = useCallback(async () => {
    if (hikeId) return hikeId;
    try {
      const res = await fetch(`${API}/api/hikes`, { headers: authHeaders() });
      if (!res.ok) return null;
      const hikes = await res.json();
      const existing = hikes.find(h => h.name === 'Travel Companion');
      if (existing) {
        setHikeId(existing._id);
        localStorage.setItem('tc_hikeId', existing._id);
        return existing._id;
      }
      const cr = await fetch(`${API}/api/hikes`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'Travel Companion', totalDistance: 1, onTrailBudget: 999999 }),
      });
      if (cr.ok) {
        const hike = await cr.json();
        setHikeId(hike._id);
        localStorage.setItem('tc_hikeId', hike._id);
        return hike._id;
      }
    } catch (e) { console.warn('ensureHike:', e.message); }
    return null;
  }, [hikeId, authHeaders]);

  // ── Load expenses from backend ──
  const loadExpenses = useCallback(async (hId) => {
    if (!hId) return;
    try {
      const res = await fetch(`${API}/api/hikes/${hId}/entries`, { headers: authHeaders() });
      if (!res.ok) return;
      const entries = await res.json();
      const mapped = {};
      entries.forEach(entry => {
        const dateKey = entry.date?.split('T')[0];
        if (!dateKey) return;
        mapped[dateKey] = {
          entryId: entry._id,
          items: (entry.expenses || []).map(exp => ({
            id: Math.random().toString(36).slice(2),
            category: mapCatFromBackend(exp.category),
            amount: exp.amount,
            desc: '',
          })),
        };
      });
      setExpenses(mapped);
    } catch (e) { console.warn('loadExpenses:', e.message); }
  }, [authHeaders]);

  // ── Load all trips list ──
  const loadTrips = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/travel/trips`, { headers: authHeaders() });
      if (!res.ok) return [];
      return await res.json();
    } catch (e) { return []; }
  }, [authHeaders]);

  // ── Load one trip by id ──
  const loadItineraryFromBackend = useCallback(async (id) => {
    try {
      const url = id ? `${API}/api/travel/itinerary/${id}` : `${API}/api/travel/itinerary`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data.days || data.days.length === 0) return null;
      return { tripId: data._id, tripName: data.tripName, days: data.days.map(day => ({ ...day, dateObj: new Date(day.date) })) };
    } catch (e) { console.warn('loadItineraryFromBackend:', e.message); return null; }
  }, [authHeaders]);

  // ── Save full itinerary to backend as a NEW trip ──
  const saveItineraryToBackend = useCallback(async (days, name) => {
    try {
      const res = await fetch(`${API}/api/travel/itinerary`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ tripName: name || 'My Trip', days: days.map(({ dateObj, ...rest }) => rest) }),
      });
      if (res.ok) {
        const saved = await res.json();
        setTripId(saved._id);
        localStorage.setItem('tc_tripId', saved._id);
        return saved._id;
      }
    } catch (e) { console.warn('saveItineraryToBackend:', e.message); }
    return null;
  }, [authHeaders]);

  // ── Save a single day update to backend ──
  const saveDayToBackend = useCallback(async (date, fields) => {
    const currentTripId = tripId || localStorage.getItem('tc_tripId');
    if (!currentTripId) return;
    try {
      await fetch(`${API}/api/travel/itinerary/${currentTripId}/${date}`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
    } catch (e) { console.warn('saveDayToBackend:', e.message); }
  }, [authHeaders, tripId]);

  // ── Weather lookup (Open-Meteo, no API key) ──
  const fetchWeather = async (location, date, fallbackLocation) => {
    const key = `${location}::${date}`;
    if (weather[key]) return; // already loaded
    setWeatherLoading(true);

    // Known city names to extract from hotel names like "Daiwa Roynet Hotel Nishi-Shinjuku"
    const KNOWN_CITIES = ['tokyo','kyoto','osaka','nagoya','hiroshima','nara','yokohama',
      'sapporo','fukuoka','okinawa','kobe','sendai','shinjuku','shibuya','akihabara',
      'paris','london','rome','milan','sydney','melbourne','new york','los angeles'];

    try {
      // Build candidate list in priority order:
      // 1. Any known city found in the hotel name (most reliable for geocoding)
      // 2. Full hotel name (sometimes works for famous hotels)
      // 3. Fallback location parts (city field split on / and ,)
      const hotelLower = location.toLowerCase();
      const citiesInHotel = KNOWN_CITIES.filter(c => hotelLower.includes(c));
      const fallbackParts = fallbackLocation
        ? fallbackLocation.split(/[/,]/).map(s => s.trim()).filter(Boolean)
        : [];
      const candidates = [...citiesInHotel, location, ...fallbackParts];

      let geoResult = null;
      for (const candidate of candidates) {
        const geoRes = await fetch(
          `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(candidate)}&count=1&language=en&format=json`
        );
        const geoData = await geoRes.json();
        if (geoData.results?.length) { geoResult = geoData.results[0]; break; }
      }
      if (!geoResult) { setWeather(w => ({ ...w, [key]: null })); return; }
      const { latitude, longitude, timezone, name: resolvedCity } = geoResult;

      // Step 2: fetch weather for that date
      const weatherRes = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,weathercode&timezone=${encodeURIComponent(timezone || 'auto')}&start_date=${date}&end_date=${date}`
      );
      const wData = await weatherRes.json();
      const d = wData.daily;
      if (!d?.temperature_2m_max?.length) { setWeather(w => ({ ...w, [key]: null })); return; }

      const code = d.weathercode[0];
      const wmoCodes = {
        0:'Clear sky', 1:'Mainly clear', 2:'Partly cloudy', 3:'Overcast',
        45:'Foggy', 48:'Icy fog', 51:'Light drizzle', 53:'Drizzle', 55:'Heavy drizzle',
        61:'Light rain', 63:'Rain', 65:'Heavy rain', 71:'Light snow', 73:'Snow', 75:'Heavy snow',
        80:'Showers', 81:'Showers', 82:'Heavy showers', 95:'Thunderstorm', 96:'Thunderstorm+hail', 99:'Thunderstorm+hail',
      };
      const wmoIcons = {
        0:'☀️', 1:'🌤️', 2:'⛅', 3:'☁️', 45:'🌫️', 48:'🌫️',
        51:'🌦️', 53:'🌦️', 55:'🌧️', 61:'🌧️', 63:'🌧️', 65:'🌧️',
        71:'🌨️', 73:'❄️', 75:'❄️', 80:'🌦️', 81:'🌧️', 82:'⛈️',
        95:'⛈️', 96:'⛈️', 99:'⛈️',
      };
      setWeather(w => ({ ...w, [key]: {
        maxTemp: Math.round(d.temperature_2m_max[0]),
        minTemp: Math.round(d.temperature_2m_min[0]),
        precip: Math.round(d.precipitation_sum[0] * 10) / 10,
        desc: wmoCodes[code] || 'Unknown',
        icon: wmoIcons[code] || '🌡️',
        city: resolvedCity,
      }}));
    } catch (e) {
      setWeather(w => ({ ...w, [key]: null }));
    } finally {
      setWeatherLoading(false);
    }
  };

  // ── Switch to a different trip ──
  const switchTrip = useCallback(async (id) => {
    setSyncLoading(true);
    const result = await loadItineraryFromBackend(id);
    if (result) {
      const toSave = result.days.map(({ dateObj, ...rest }) => rest);
      localStorage.setItem('tc_itinerary', JSON.stringify(toSave));
      localStorage.setItem('tc_tripId', result.tripId);
      setTripId(result.tripId);
      setItinerary(result.days);
      const today = new Date().toISOString().split('T')[0];
      const first = result.days.find(d => d.date === today) || result.days[0];
      setCurrentDay(first);
      setExpenses({});
      setShowTripManager(false);
    }
    setSyncLoading(false);
  }, [loadItineraryFromBackend]);

  useEffect(() => {
    const init = async () => {
      // Clear localStorage if a different user has logged in
      const storedUserId = localStorage.getItem('tc_userId');
      if (storedUserId && storedUserId !== String(user._id || user.id || user.username)) {
        ['tc_itinerary','tc_tripId','tc_hikeId','tc_theme'].forEach(k => localStorage.removeItem(k));
        setItinerary([]);
        setCurrentDay(null);
        setTripId(null);
        setHikeId(null);
      }
      localStorage.setItem('tc_userId', String(user._id || user.id || user.username));

      const hId = await ensureHike();
      if (hId) loadExpenses(hId);

      // Load trips list for the manager
      const allTrips = await loadTrips();
      setTrips(allTrips);

      // Load the last-used trip for THIS user from backend
      const savedTripId = localStorage.getItem('tc_tripId');
      const result = await loadItineraryFromBackend(savedTripId || null);
      if (result && result.days.length > 0) {
        const toSave = result.days.map(({ dateObj, ...rest }) => rest);
        localStorage.setItem('tc_itinerary', JSON.stringify(toSave));
        localStorage.setItem('tc_tripId', result.tripId);
        setTripId(result.tripId);
        setItinerary(result.days);
        setCurrentDay(prev => {
          if (prev) return result.days.find(d => d.date === prev.date) || result.days[0];
          const today = new Date().toISOString().split('T')[0];
          return result.days.find(d => d.date === today) || result.days.find(d => d.activities || d.hotel) || result.days[0];
        });
      } else {
        // No backend data for this user — clear any stale localStorage
        localStorage.removeItem('tc_itinerary');
        localStorage.removeItem('tc_tripId');
        setItinerary([]);
        setCurrentDay(null);
      }
      setSyncLoading(false);
    };
    if (user) init();
    else {
      // Logged out — clear everything
      ['tc_itinerary','tc_tripId','tc_hikeId','tc_userId'].forEach(k => localStorage.removeItem(k));
      setItinerary([]);
      setCurrentDay(null);
      setTripId(null);
      setSyncLoading(false);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save expenses to backend ──
  const saveExpenses = async (dateKey, items, currentHikeId, currentEntryId) => {
    const hId = currentHikeId || hikeId;
    if (!hId) return { ok: false };
    const backendExpenses = items.map(i => ({ category: mapCatToBackend(i.category), amount: parseFloat(i.amount) || 0 }));
    const total = backendExpenses.reduce((s, e) => s + e.amount, 0);
    const payload = {
      date: dateKey, kmTravelled: 0, rpe: 5, mood: 5, sleepQuality: 5,
      overallFeeling: 5, caloriesSpent: 0, weatherTemp: 'Mild', weatherType: 'Sunny',
      expenses: backendExpenses, moneySpent: total, notes: '',
    };
    try {
      let res;
      if (currentEntryId) {
        res = await fetch(`${API}/api/hikes/${hId}/entries/${currentEntryId}`, {
          method: 'PUT',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${API}/api/hikes/${hId}/entries`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          const saved = await res.json();
          return { ok: true, entryId: saved._id };
        }
      }
      return { ok: res.ok };
    } catch (e) { return { ok: false }; }
  };

  // ── Excel parsing ──
  const handleFile = (file) => {
    if (!file) return;
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      parsePDF(file);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        parseItinerary(rows); // async — AI-powered
      } catch (err) { alert('Could not parse file: ' + err.message); }
    };
    reader.readAsArrayBuffer(file);
  };

  // ── PDF parsing — upload file to backend, which calls Anthropic ──
  const parsePDF = async (file) => {
    setAiLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API}/api/travel/ai/parse-pdf`, {
        method: 'POST',
        headers: authHeaders(), // do NOT set Content-Type — browser sets multipart boundary
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        alert(`PDF parsing error: ${data.message || res.statusText}`); return;
      }
      if (!data.content || data.content.length === 0) {
        alert(`PDF parsing error: empty response (stop_reason: ${data.stop_reason})`); return;
      }

      const text = data.content?.[0]?.text || '';
      console.log('[parsePDF] raw response:', text);
      const clean = text.replace(/```json|```/g, '').trim();

      let parsed;
      try {
        parsed = JSON.parse(clean);
      } catch (e) {
        alert(`PDF parsing error: Claude returned non-JSON.\n\n${text.slice(0, 300)}`); return;
      }

      if (!Array.isArray(parsed) || parsed.length === 0) {
        alert('Could not find an itinerary in this PDF.'); return;
      }

      const withDates = parsed.map(entry => {
        const dateObj = new Date(entry.date);
        return { ...entry, dateObj: isNaN(dateObj) ? new Date() : dateObj };
      }).filter(e => !isNaN(e.dateObj));

      if (withDates.length === 0) { alert('No valid dates found in PDF.'); return; }

      const allText = withDates.map(r => `${r.location} ${r.activities} ${r.hotel}`).join(' ').toLowerCase();
      const detectedKey = detectCountry(allText);
      const detectedTheme = COUNTRY_THEMES[detectedKey] || COUNTRY_THEMES.japan;
      setTheme(detectedTheme);
      localStorage.setItem('tc_theme', detectedKey);

      const toSave = withDates.map(({ dateObj, ...rest }) => rest);
      localStorage.setItem('tc_itinerary', JSON.stringify(toSave));
      setItinerary(withDates);

      const today = new Date().toISOString().split('T')[0];
      const first = withDates.find(d => d.date === today) || withDates[0];
      setCurrentDay(first);

      if (user) {
        const name = window.prompt('Name this trip:', file.name.replace('.pdf', '') || 'My Trip') || 'My Trip';
        saveItineraryToBackend(withDates, name).then(newId => {
          if (newId) loadTrips().then(setTrips);
        });
      }
    } catch (err) {
      alert('PDF parsing failed: ' + err.message);
    } finally {
      setAiLoading(false);
    }
  };

  const parseItinerary = async (rows) => {
    setAiLoading(true);
    try {
      // Send raw rows to backend — it builds the prompt and calls Anthropic.
      const res = await fetch(`${API}/api/travel/ai/parse-excel`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows }),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(`Excel parsing error: ${data.message || res.statusText}`); return;
      }

      const text = data.content?.[0]?.text || '';
      const clean = text.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(clean);

      if (!Array.isArray(parsed) || parsed.length === 0) {
        alert('AI could not extract any itinerary rows from this file.'); return;
      }

      // Ensure dateObj is attached
      const withDates = parsed.map(entry => {
        const dateObj = new Date(entry.date);
        return { ...entry, dateObj: isNaN(dateObj) ? new Date() : dateObj };
      }).filter(e => !isNaN(e.dateObj));

      if (withDates.length === 0) { alert('No valid dates found after AI parsing.'); return; }

      // Detect country — use word-boundary regex so 'uk' doesn't match inside 'tsukiji', 'osaka' etc.
      const allText = withDates.map(r => `${r.location} ${r.activities} ${r.hotel}`).join(' ').toLowerCase();
      // Also check for Japanese city names explicitly
      const detectedKey = detectCountry(allText);
      const detectedTheme = COUNTRY_THEMES[detectedKey] || COUNTRY_THEMES.japan;
      setTheme(detectedTheme);
      localStorage.setItem('tc_theme', detectedKey);

      const toSave = withDates.map(({ dateObj, ...rest }) => rest);
      localStorage.setItem('tc_itinerary', JSON.stringify(toSave));
      setItinerary(withDates);

      const today = new Date().toISOString().split('T')[0];
      const todayEntry = withDates.find(d => d.date === today);
      const first = todayEntry || withDates.find(d => d.activities || d.hotel) || withDates[0];
      setCurrentDay(first);

      if (user) {
        const name = window.prompt('Name this trip:', 'My Trip') || 'My Trip';
        saveItineraryToBackend(withDates, name).then(newId => {
          if (newId) loadTrips().then(setTrips);
        });
      }
    } catch (err) {
      alert('AI parsing failed: ' + err.message);
    } finally {
      setAiLoading(false);
    }
  };

  // ── Add expense ──
  const addExpense = async () => {
    const amount = parseFloat(expAmount);
    if (!amount || amount <= 0 || !currentDay) return;
    setExpSaving(true);

    const newItem = { id: Math.random().toString(36).slice(2), category: expCat, desc: expDesc, amount };
    const dateKey = currentDay.date;
    const existing = expenses[dateKey] || { entryId: null, items: [] };
    const newItems = [...existing.items, newItem];

    const result = await saveExpenses(dateKey, newItems, hikeId, existing.entryId);
    const newEntry = { entryId: result.entryId || existing.entryId, items: newItems };
    setExpenses(prev => ({ ...prev, [dateKey]: newEntry }));
    setExpCat('General'); setExpDesc(''); setExpAmount('');
    setExpStatus(result.ok ? 'Saved ✓' : 'Saved locally');
    setExpSaving(false);
    setTimeout(() => setExpStatus(''), 3000);
  };

  // ── Delete expense ──
  const deleteExpense = async (dateKey, itemId) => {
    const existing = expenses[dateKey];
    if (!existing) return;
    const newItems = existing.items.filter(i => i.id !== itemId);
    await saveExpenses(dateKey, newItems, hikeId, existing.entryId);
    setExpenses(prev => ({ ...prev, [dateKey]: { ...existing, items: newItems } }));
  };

  // ── AI Suggestions ──
  const loadSuggestions = async () => {
    if (!currentDay) return;
    setSugLoading(true);
    const { date, location } = currentDay;
    const place = location || 'Tokyo, Japan';

    try {
      const res = await fetch(`${API}/api/travel/ai/suggestions`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ location: place, customPrompt }),
      });

      if (res.ok) {
        const data = await res.json();
        const textBlocks = data.content.filter(b => b.type === 'text');
        const text = textBlocks.map(b => b.text || '').join('');
        const match = text.match(/\[[\s\S]*\]/);
        if (match) {
          const parsed = JSON.parse(match[0]);
          setSuggestions(prev => ({ ...prev, [date]: parsed }));
          setSugLoading(false);
          return;
        }
      }
    } catch (e) { /* fall through to static */ }

    // Static fallback
    const locLower = (location || '').toLowerCase();
    let staticList = STATIC_SUGGESTIONS.default;
    for (const [key, val] of Object.entries(STATIC_SUGGESTIONS)) {
      if (locLower.includes(key)) { staticList = val; break; }
    }
    setSuggestions(prev => ({ ...prev, [date]: staticList.map(s => ({ name: s, desc: 'A recommended local experience.' })) }));
    setSugLoading(false);
  };

  // ── Currency converter (Open Exchange Rates — free, no key) ──
  const CURRENCIES = ['AUD','EUR','GBP','HKD','IDR','INR','JPY','KRW','MYR','NZD','SGD','THB','TWD','USD','VND'];

  const doConvert = (amt, from, to, rates) => {
    const a = parseFloat(amt);
    if (!a || !rates) return;
    let result;
    if (rates.base === from) result = a * (rates.rates[to] || 1);
    else if (rates.base === to) result = a / (rates.rates[from] || 1);
    else result = a * (rates.rates[to] / rates.rates[from]);
    setFxResult(result);
  };

  const handleConvert = async () => {
    if (!fxAmount) return;
    let rates = fxRates;
    if (!rates || rates.base !== fxFrom) {
      setFxLoading(true);
      try {
        const res = await fetch(`https://open.er-api.com/v6/latest/${fxFrom}`);
        const data = await res.json();
        if (data.rates) { rates = { base: fxFrom, rates: data.rates }; setFxRates(rates); }
      } catch (e) { console.warn('fetchRates:', e); }
      finally { setFxLoading(false); }
    }
    doConvert(fxAmount, fxFrom, fxTo, rates);
  };

  // ── Fetch weather when day changes ──
  useEffect(() => {
    if (currentDay?.location && currentDay?.date) {
      // Use hotel as primary location (more precise), fall back to day location
      const weatherLocation = currentDay.hotel || currentDay.location;
      if (weatherLocation && currentDay.date) {
        fetchWeather(weatherLocation, currentDay.date, currentDay.location);
      }
    }
  }, [currentDay?.location, currentDay?.date]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived ──
  const dayExpenses   = currentDay ? (expenses[currentDay.date] || { items: [] }) : { items: [] };
  const dayTotal      = dayExpenses.items.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  // Auto-convert dayTotal to AUD using cached fx rates if available
  const dayTotalAUD = (() => {
    if (!dayTotal || !fxRates) return null;
    const rates = fxRates.rates;
    if (fxRates.base === 'JPY') return dayTotal * (rates['AUD'] || 0);
    if (fxRates.base === 'AUD') return dayTotal / (rates['JPY'] || 1);
    if (rates['JPY'] && rates['AUD']) return dayTotal * (rates['AUD'] / rates['JPY']);
    return null;
  })();
  const daySuggestions= currentDay ? suggestions[currentDay.date] : null;

  // ── Styles (inline so no external CSS file needed) ──
  // ── Colour tokens (dark / light) ──
  const c = darkMode ? {
    pageBg:    '#0d0f14',
    cardBg:    '#13161e',
    cardBg2:   '#1a1e2a',
    border:    '#52586e',
    text:      '#e8e0d4',
    textDim:   '#9da0ad',
    textFaint: '#7a7d8a',
    inputBg:   '#0d0f14',
    statsBg:   '#1a1e2a',
    topBarBg:  'rgba(13,15,20,0.95)',
    sugItemBg: '#1a1e2a',
    expEntryBg:'#1a1e2a',
    headerGrad:'linear-gradient(135deg, #13161e 0%, #1a1e2a 100%)',
  } : {
    pageBg:    '#f4f1eb',
    cardBg:    '#ffffff',
    cardBg2:   '#f9f7f3',
    border:    '#c8c2b4',
    text:      '#1a1714',
    textDim:   '#6b6560',
    textFaint: '#9a948c',
    inputBg:   '#f4f1eb',
    statsBg:   '#f0ece4',
    topBarBg:  'rgba(244,241,235,0.97)',
    sugItemBg: '#f0ece4',
    expEntryBg:'#f0ece4',
    headerGrad:'linear-gradient(135deg, #ffffff 0%, #f4f1eb 100%)',
  };

  const s = {
    page: {
      minHeight: '100vh',
      background: c.pageBg,
      color: c.text,
      fontFamily: "'DM Mono', 'Courier New', monospace",
      overflowX: 'hidden',
      width: '100%',
      boxSizing: 'border-box',
    },
    topBar: {
      background: c.topBarBg,
      borderBottom: `1px solid ${c.border}`,
      height: 56,
      position: 'sticky', top: 0, zIndex: 50,
    },
    topBarInner: {
      maxWidth: 860, margin: '0 auto', padding: '0 16px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      height: '100%',
    },
    topBarTitle: {
      fontFamily: 'Georgia, serif', fontSize: '1rem',
      color: theme.accent2, letterSpacing: '0.05em',
    },
    topBarRight: { display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.75rem', color: c.textDim },
    flagText: { fontSize: '1.3rem', opacity: 0.7 },
    modeBtn: {
      background: 'none', border: `1px solid ${c.border}`, color: c.textDim,
      fontFamily: 'inherit', fontSize: '0.7rem', padding: '4px 10px',
      borderRadius: 2, cursor: 'pointer',
    },

    main: { maxWidth: 860, margin: '0 auto', padding: '24px 16px 80px' },

    uploadZone: {
      border: `2px dashed ${dragOver ? theme.accent2 : c.border}`,
      borderRadius: 6, padding: '48px 24px', textAlign: 'center', cursor: 'pointer',
      background: dragOver ? `${theme.accent2}08` : 'transparent',
      transition: 'all 0.2s',
    },
    uploadIcon: { fontSize: '2.5rem', marginBottom: 12 },
    uploadTitle: { fontSize: '0.95rem', marginBottom: 6 },
    uploadHint: { fontSize: '0.72rem', color: c.textDim },

    selectorWrap: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' },
    selectorLabel: { fontSize: '0.62rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: c.textDim, whiteSpace: 'nowrap' },
    select: {
      background: c.cardBg, border: `1px solid ${c.border}`, color: c.text,
      padding: '8px 12px', fontFamily: 'inherit', fontSize: '0.78rem',
      borderRadius: 2, outline: 'none', flex: 1, minWidth: 200, cursor: 'pointer',
    },
    btnSmall: {
      padding: '7px 14px', background: 'none', border: `1px solid ${c.border}`,
      color: c.textDim, fontFamily: 'inherit', fontSize: '0.7rem',
      borderRadius: 2, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s',
    },

    card: { background: c.cardBg, border: `1px solid ${c.border}`, borderRadius: 4, overflow: 'hidden' },
    cardHeader: {
      padding: '20px 24px 16px',
      borderBottom: `1px solid ${c.border}`,
      background: c.headerGrad,
      borderLeft: `3px solid ${theme.accent}`,
      paddingLeft: 21,
    },
    cardMeta: { fontSize: '0.62rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: theme.accent, marginBottom: 4 },
    cardTitle: { fontFamily: 'Georgia, serif', fontSize: '1.6rem', lineHeight: 1.2, marginBottom: 6 },
    cardLoc: { fontSize: '0.72rem', color: c.textDim, display: 'flex', alignItems: 'center', gap: 6 },
    locDot: { width: 6, height: 6, borderRadius: '50%', background: theme.accent2, display: 'inline-block' },

    statsBar: {
      display: 'flex', gap: 20, flexWrap: 'wrap',
      padding: '12px 24px', borderBottom: `1px solid ${c.border}`, background: c.statsBg,
    },
    statVal: { fontFamily: 'Georgia, serif', fontSize: '0.95rem', color: theme.accent2 },
    statKey: { fontSize: '0.58rem', color: c.textFaint, textTransform: 'uppercase', letterSpacing: '0.1em' },

    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', borderTop: `1px solid ${c.border}` },
    block: { padding: '20px 24px', borderRight: `1px solid ${c.border}`, borderBottom: `1px solid ${c.border}` },
    blockFull: { padding: '20px 24px', borderBottom: `1px solid ${c.border}`, gridColumn: '1 / -1' },
    sectionLabel: {
      fontSize: '0.58rem', letterSpacing: '0.2em', textTransform: 'uppercase',
      color: theme.accent2, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8,
    },

    hotelName: { fontFamily: 'Georgia, serif', fontSize: '1rem', marginBottom: 8, lineHeight: 1.4 },
    bookedBadge: {
      display: 'inline-block', padding: '2px 8px',
      background: 'rgba(76,175,125,0.15)', border: '1px solid rgba(76,175,125,0.3)',
      color: '#4caf7d', fontSize: '0.58rem', letterSpacing: '0.1em',
      textTransform: 'uppercase', borderRadius: 2, marginBottom: 8,
    },
    costRow: { display: 'flex', gap: 16, marginBottom: 10, flexWrap: 'wrap' },
    costLabel: { fontSize: '0.58rem', color: c.textFaint, textTransform: 'uppercase', letterSpacing: '0.08em' },
    costVal: { color: theme.accent2, fontSize: '0.82rem' },
    linkBtn: {
      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
      border: `1px solid ${c.border}`, color: c.textDim, textDecoration: 'none',
      fontSize: '0.72rem', borderRadius: 2, marginBottom: 6, transition: 'all 0.2s',
      background: 'none', cursor: 'pointer', width: '100%',
    },

    activitiesText: { fontFamily: 'Georgia, serif', fontSize: '0.95rem', lineHeight: 1.7, color: c.text },
    notesText: { fontSize: '0.75rem', color: c.textDim, lineHeight: 1.6, fontStyle: 'italic', marginTop: 10 },
    planEditBtn: {
      background: 'none', border: 'none', color: c.textDim, cursor: 'pointer',
      fontSize: '0.7rem', padding: '0 6px', opacity: 0.7,
    },
    planTextarea: {
      width: '100%', background: c.inputBg, border: `1px solid ${c.border}`,
      color: c.text, padding: '10px', fontFamily: 'Georgia, serif',
      fontSize: '0.95rem', lineHeight: 1.7, borderRadius: 2,
      outline: 'none', resize: 'vertical', minHeight: 100, boxSizing: 'border-box',
    },
    planSaveBtn: {
      marginTop: 8, padding: '6px 16px', background: theme.accent, border: 'none',
      color: 'white', fontFamily: 'inherit', fontSize: '0.7rem', borderRadius: 2,
      cursor: 'pointer', marginRight: 6,
    },
    planCancelBtn: {
      marginTop: 8, padding: '6px 16px', background: 'none', border: `1px solid ${c.border}`,
      color: c.textDim, fontFamily: 'inherit', fontSize: '0.7rem', borderRadius: 2, cursor: 'pointer',
    },

    sugItem: {
      padding: '10px 12px', background: c.sugItemBg,
      borderLeft: `2px solid ${theme.accent2}`,
      borderRadius: '0 2px 2px 0', marginBottom: 8,
      cursor: 'pointer', transition: 'opacity 0.15s',
    },
    sugName: { fontWeight: 'bold', color: theme.accent2, fontSize: '0.8rem' },
    sugDesc: { color: '#b0b3be', fontSize: '0.7rem', marginTop: 3 },
    btnSuggest: {
      padding: '8px 14px', background: 'none',
      border: `1px dashed ${c.border}`, color: c.textDim,
      fontFamily: 'inherit', fontSize: '0.72rem',
      cursor: 'pointer', borderRadius: 2, transition: 'all 0.2s',
    },

    expTotal: { fontFamily: 'Georgia, serif', fontSize: '1.5rem', color: theme.accent2 },
    expTotalLabel: { fontSize: '0.58rem', color: c.textFaint, textTransform: 'uppercase' },
    expRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 },
    expInput: {
      background: c.inputBg, border: `1px solid ${c.border}`, color: c.text,
      padding: '7px 10px', fontFamily: 'inherit', fontSize: '0.75rem',
      borderRadius: 2, outline: 'none',
    },
    expEntry: {
      display: 'flex', alignItems: 'center', padding: '6px 10px',
      background: c.expEntryBg, borderRadius: 2, marginBottom: 4, fontSize: '0.72rem',
    },
    expCatLabel: { color: c.textDim, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.1em', minWidth: 72 },
    expDesc: { flex: 1, color: c.text, margin: '0 8px' },
    expAmt: { color: theme.accent2, fontWeight: 'bold', whiteSpace: 'nowrap' },
    expDel: { background: 'none', border: 'none', color: c.textFaint, cursor: 'pointer', marginLeft: 8, fontSize: '0.85rem' },
    btnAddExp: {
      width: '100%', padding: 9, background: theme.accent, border: 'none', color: 'white',
      fontFamily: 'inherit', fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase',
      cursor: 'pointer', borderRadius: 2, marginTop: 4, opacity: expSaving ? 0.6 : 1,
    },
    noData: { color: c.textFaint, fontSize: '0.75rem', fontStyle: 'italic' },
    statusOk: { fontSize: '0.65rem', color: '#4caf7d', marginTop: 4 },
    statusErr: { fontSize: '0.65rem', color: '#e05555', marginTop: 4 },
  };

  const divider = <span style={{ flex: 1, height: 1, background: c.border, display: 'inline-block', marginLeft: 8 }} />;

  // ════════════════════════════ RENDER ════════════════════════════
  return (
    <div style={s.page}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } input[type=number]::-webkit-inner-spin-button, input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }`}</style>

      {/* ── Trip Manager Modal ── */}
      {showTripManager && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: darkMode ? '#1a1e2a' : '#fff', borderRadius: 14, padding: 28, width: '100%', maxWidth: 480, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.4)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: darkMode ? '#e8e0d4' : '#2a2520' }}>✈ My Trips</div>
              <button onClick={() => setShowTripManager(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: darkMode ? '#9da0ad' : '#6b6560' }}>✕</button>
            </div>

            {trips.length === 0 && (
              <div style={{ color: darkMode ? '#9da0ad' : '#6b6560', fontSize: '0.85rem', textAlign: 'center', padding: '20px 0' }}>No saved trips yet.</div>
            )}

            {trips.map(t => (
              <div key={t._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, marginBottom: 8, background: t._id === tripId ? (darkMode ? '#252a3a' : '#f0ede8') : 'transparent', border: `1px solid ${darkMode ? '#52586e' : '#c8c2b4'}` }}>
                {renamingTrip === t._id ? (
                  <>
                    <input
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          fetch(`${API}/api/travel/itinerary/${t._id}/rename`, { method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ tripName: renameValue }) })
                            .then(() => loadTrips().then(setTrips));
                          setRenamingTrip(null);
                        }
                        if (e.key === 'Escape') setRenamingTrip(null);
                      }}
                      autoFocus
                      style={{ flex: 1, background: darkMode ? '#0d0f14' : '#fff', border: `1px solid ${darkMode ? '#52586e' : '#c8c2b4'}`, borderRadius: 5, padding: '4px 8px', color: darkMode ? '#e8e0d4' : '#2a2520', fontSize: '0.85rem' }}
                    />
                    <button onClick={() => {
                      fetch(`${API}/api/travel/itinerary/${t._id}/rename`, { method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ tripName: renameValue }) })
                        .then(() => loadTrips().then(setTrips));
                      setRenamingTrip(null);
                    }} style={{ fontSize: '0.75rem', padding: '4px 8px', borderRadius: 5, border: 'none', background: theme.accent, color: '#fff', cursor: 'pointer' }}>Save</button>
                  </>
                ) : (
                  <>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem', color: darkMode ? '#e8e0d4' : '#2a2520' }}>{t.tripName}</div>
                      <div style={{ fontSize: '0.72rem', color: darkMode ? '#9da0ad' : '#6b6560' }}>{t.dateRange} · {t.dayCount} days</div>
                    </div>
                    <button onClick={() => { setRenamingTrip(t._id); setRenameValue(t.tripName); }}
                      style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: 5, border: `1px solid ${darkMode ? '#52586e' : '#c8c2b4'}`, background: 'none', color: darkMode ? '#9da0ad' : '#6b6560', cursor: 'pointer' }}>✎</button>
                    {t._id !== tripId && (
                      <button onClick={() => switchTrip(t._id)}
                        style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: 5, border: 'none', background: theme.accent, color: '#fff', cursor: 'pointer' }}>Load</button>
                    )}
                    {t._id === tripId && (
                      <span style={{ fontSize: '0.72rem', color: theme.accent, fontWeight: 600 }}>Active</span>
                    )}
                    <button onClick={() => {
                      if (!window.confirm(`Delete "${t.tripName}"?`)) return;
                      fetch(`${API}/api/travel/itinerary/${t._id}`, { method: 'DELETE', headers: authHeaders() })
                        .then(() => {
                          loadTrips().then(setTrips);
                          if (t._id === tripId) {
                            setItinerary([]); setCurrentDay(null); setTripId(null);
                            localStorage.removeItem('tc_itinerary'); localStorage.removeItem('tc_tripId');
                          }
                        });
                    }} style={{ fontSize: '0.72rem', padding: '3px 8px', borderRadius: 5, border: 'none', background: '#c0392b', color: '#fff', cursor: 'pointer' }}>✕</button>
                  </>
                )}
              </div>
            ))}

            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${darkMode ? '#52586e' : '#c8c2b4'}`, fontSize: '0.78rem', color: darkMode ? '#9da0ad' : '#6b6560', textAlign: 'center' }}>
              To add a new trip, close this and upload a new Excel file.
            </div>
          </div>
        </div>
      )}
      {/* Top bar */}
      <div style={s.topBar}>
        <div style={s.topBarInner}>
          <div style={s.topBarTitle}>
            Travel Companion &nbsp;
            <span style={{ color: '#9da0ad', fontSize: '0.72rem', fontFamily: 'inherit' }}>{theme.label}</span>
          </div>
          <div style={s.topBarRight}>
            {user && (
              <button style={s.modeBtn} onClick={() => { loadTrips().then(setTrips); setShowTripManager(true); }}>
                ✈ My Trips
              </button>
            )}
            <button style={s.modeBtn} onClick={() => {
              const next = !darkMode;
              setDarkMode(next);
              localStorage.setItem('tc_darkmode', String(next));
            }}>
              {darkMode ? '☀ Light' : '☾ Dark'}
            </button>
            <span>{user?.username}</span>
            <span style={s.flagText}>{theme.flag}</span>
          </div>
        </div>
      </div>

      <div style={s.main}>
        {/* ── Upload ── */}
        {itinerary.length === 0 && syncLoading && (
          <div style={{...s.uploadZone, cursor: 'default'}}>
            <div style={{ fontSize: '2rem', marginBottom: 12, animation: 'spin 1.2s linear infinite', display: 'inline-block' }}>✦</div>
            <div style={s.uploadTitle}>Loading your trip…</div>
            <div style={s.uploadHint}>Fetching from the cloud</div>
          </div>
        )}
        {itinerary.length === 0 && !syncLoading && (
          <div
            style={s.uploadZone}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => !aiLoading && document.getElementById('tc-file-input').click()}
          >
            {aiLoading ? (
              <>
                <div style={{ fontSize: '2rem', marginBottom: 12, animation: 'spin 1.2s linear infinite', display: 'inline-block' }}>✦</div>
                <div style={s.uploadTitle}>Reading your itinerary…</div>
                <div style={s.uploadHint}>AI is parsing your file — this takes a few seconds</div>
              </>
            ) : (
              <>
                <div style={s.uploadIcon}>📋</div>
                <div style={s.uploadTitle}>Load your itinerary</div>
                <div style={s.uploadHint}>Drop your Excel or PDF file here, or click to browse</div>
              </>
            )}
            <input id="tc-file-input" type="file" accept=".xlsx,.xls,.csv,.pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,application/pdf" style={{ display: 'none' }}
              onChange={e => handleFile(e.target.files[0])} />
          </div>
        )}

        {/* ── Day view ── */}
        {itinerary.length > 0 && (
          <>
            {/* Day selector */}
            <div style={s.selectorWrap}>
              <span style={s.selectorLabel}>Select Day</span>
              <select
                style={s.select}
                value={currentDay?.date || ''}
                onChange={e => setCurrentDay(itinerary.find(d => d.date === e.target.value))}
              >
                {itinerary.map(day => {
                  const label = day.dateObj.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
                  const preview = day.activities ? ' — ' + day.activities.slice(0, 35) + (day.activities.length > 35 ? '…' : '') : '';
                  return (
                    <option key={day.date} value={day.date}>
                      {label}{day.location ? ' · ' + day.location : ''}{preview}
                    </option>
                  );
                })}
              </select>
              <button style={s.btnSmall} onClick={() => {
                // Just clear local state — trip stays saved in backend
                setItinerary([]);
                setCurrentDay(null);
                setTripId(null);
                localStorage.removeItem('tc_itinerary');
                localStorage.removeItem('tc_tripId');
              }}>↑ Add Trip</button>
            </div>

            {/* Day card */}
            {currentDay && (
              <div style={s.card}>
                {/* Header */}
                <div style={s.cardHeader}>
                  <div style={s.cardMeta}>
                    {currentDay.dateObj.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                  <div style={s.cardTitle}>
                    {currentDay.dateObj.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </div>
                  {currentDay.location && (
                    <div style={s.cardLoc}>
                      <span style={s.locDot} />
                      {currentDay.location}
                    </div>
                  )}
                </div>

                {/* Stats bar */}
                <div style={s.statsBar}>
                  <div>
                    <div style={s.statVal}>
                      ¥{dayTotal.toLocaleString()}
                      {dayTotalAUD !== null && (
                        <span style={{ fontSize: '0.72rem', color: c.textDim, marginLeft: 8 }}>
                          ≈ A${dayTotalAUD.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      )}
                    </div>
                    <div style={s.statKey}>Today's spend</div>
                  </div>
                  {currentDay.accomJPY > 0 && (
                    <div>
                      <div style={s.statVal}>¥{currentDay.accomJPY.toLocaleString()}</div>
                      <div style={s.statKey}>Accom cost</div>
                    </div>
                  )}
                  {currentDay.booked && (
                    <div>
                      <div style={{ ...s.statVal, color: '#4caf7d' }}>✓ Booked</div>
                      <div style={s.statKey}>Accommodation</div>
                    </div>
                  )}
                </div>

                {/* Sections grid */}
                <div style={s.grid}>

                  {/* Accommodation */}
                  <div style={s.block}>
                    <div style={s.sectionLabel}>Accommodation {divider}</div>

                    {/* Weather widget */}
                    {currentDay.location && (() => {
                      const wKey = `${currentDay.hotel || currentDay.location}::${currentDay.date}`;
                      const w = weather[wKey];
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: darkMode ? '#0d0f14' : '#f0ede8', marginBottom: 10, border: `1px solid ${darkMode ? '#52586e' : '#c8c2b4'}` }}>
                          {w === undefined ? (
                            <span style={{ fontSize: '0.78rem', color: darkMode ? '#7a7d8a' : '#9a948c' }}>
                              {weatherLoading ? '⏳ Loading weather…' : '🌡️ Fetching weather…'}
                            </span>
                          ) : w === null ? (
                            <span style={{ fontSize: '0.78rem', color: darkMode ? '#7a7d8a' : '#9a948c' }}>🌡️ Weather unavailable</span>
                          ) : (
                            <>
                              <span style={{ fontSize: '1.6rem', lineHeight: 1 }}>{w.icon}</span>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: '0.88rem', color: darkMode ? '#e8e0d4' : '#2a2520' }}>
                                  {w.desc}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: darkMode ? '#9da0ad' : '#6b6560' }}>
                                  {w.maxTemp}° / {w.minTemp}°C
                                  {w.precip > 0 && ` · 💧 ${w.precip}mm`}
                                  {w.city && <span style={{ marginLeft: 6, opacity: 0.7 }}>{w.city}</span>}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })()}

                    {currentDay.hotel ? (
                      <>
                        <div style={s.hotelName}>{currentDay.hotel}</div>
                        {currentDay.booked && <div style={s.bookedBadge}>✓ Booked</div>}
                        {(currentDay.accomJPY > 0 || currentDay.accomAUD > 0) && (
                          <div style={s.costRow}>
                            {currentDay.accomJPY > 0 && (
                              <div>
                                <div style={s.costLabel}>JPY</div>
                                <div style={s.costVal}>¥{currentDay.accomJPY.toLocaleString()}</div>
                              </div>
                            )}
                            {currentDay.accomAUD > 0 && (
                              <div>
                                <div style={s.costLabel}>AUD</div>
                                <div style={s.costVal}>A${currentDay.accomAUD.toLocaleString()}</div>
                              </div>
                            )}
                          </div>
                        )}
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(currentDay.hotel + ' ' + currentDay.location)}`}
                          target="_blank" rel="noopener noreferrer" style={s.linkBtn}
                        >
                          📍 Open in Google Maps
                        </a>
                        <a
                          href={`https://www.google.com/search?q=${encodeURIComponent(currentDay.hotel)}`}
                          target="_blank" rel="noopener noreferrer" style={s.linkBtn}
                        >
                          🔗 Search Hotel Online
                        </a>
                      </>
                    ) : (
                      <span style={s.noData}>No accommodation for this day</span>
                    )}
                  </div>

                  {/* Today's Plan */}
                  <div style={s.block}>
                    <div style={s.sectionLabel}>
                      Today's Plan {divider}
                      <button style={s.planEditBtn} onClick={() => {
                        setPlanDraft(currentDay.activities || '');
                        setEditingPlan(true);
                      }} title="Edit plan">✎</button>
                    </div>
                    {editingPlan ? (
                      <div>
                        <textarea
                          style={s.planTextarea}
                          value={planDraft}
                          onChange={e => setPlanDraft(e.target.value)}
                          autoFocus
                        />
                        <button style={s.planSaveBtn} onClick={() => {
                          const updated = itinerary.map(d =>
                            d.date === currentDay.date ? { ...d, activities: planDraft } : d
                          );
                          setItinerary(updated);
                          setCurrentDay({ ...currentDay, activities: planDraft });
                          const toSave = updated.map(({ dateObj, ...rest }) => rest);
                          localStorage.setItem('tc_itinerary', JSON.stringify(toSave));
                          saveDayToBackend(currentDay.date, { activities: planDraft });
                          setEditingPlan(false);
                        }}>Save</button>
                        <button style={s.planCancelBtn} onClick={() => setEditingPlan(false)}>Cancel</button>
                      </div>
                    ) : (
                      <>
                        {currentDay.activities
                          ? <div style={s.activitiesText}>{currentDay.activities}</div>
                          : <span style={s.noData}>No planned activities</span>
                        }
                        {currentDay.notes && <div style={s.notesText}>{currentDay.notes}</div>}
                      </>
                    )}
                  </div>

                  {/* Suggested Attractions */}
                  <div style={s.blockFull}>
                    <div style={s.sectionLabel}>Suggested Attractions {divider}</div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
                      <span style={{ fontSize: '0.65rem', color: '#9da0ad', whiteSpace: 'nowrap' }}>AI focus</span>
                      <input
                        type="text"
                        placeholder="e.g. cycling, street food, war history…"
                        value={customPrompt}
                        onChange={e => setCustomPrompt(e.target.value)}
                        style={{ flex: 1, background: '#13161e', border: '1px solid #52586e', color: '#e8e0d4', padding: '7px 10px', fontFamily: 'inherit', fontSize: '0.72rem', borderRadius: 2, outline: 'none' }}
                      />
                      <button
                        style={s.btnSuggest}
                        disabled={sugLoading}
                        onClick={loadSuggestions}
                      >
                        {sugLoading ? 'Thinking…' : daySuggestions ? '↺ Refresh' : '✦ Suggest'}
                      </button>
                    </div>
                    {daySuggestions && daySuggestions.length > 0 ? (
                      daySuggestions.map((sug, i) => (
                        <div key={i} style={s.sugItem}
                          onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                            <div style={s.sugName}>{sug.name}</div>
                            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(sug.name + ' ' + (currentDay.location || ''))}`}
                                target="_blank" rel="noopener noreferrer"
                                style={{ fontSize: '0.65rem', color: theme.accent2, textDecoration: 'none', opacity: 0.8 }}
                                title="Open in Google Maps"
                              >📍 Maps</a>
                              <a
                                href={`https://www.google.com/search?q=${encodeURIComponent(sug.name + ' ' + (currentDay.location || ''))}`}
                                target="_blank" rel="noopener noreferrer"
                                style={{ fontSize: '0.65rem', color: theme.accent2, textDecoration: 'none', opacity: 0.8 }}
                                title="Search online"
                              >🔍 Search</a>
                            </div>
                          </div>
                          <div style={s.sugDesc}>{sug.desc}</div>
                        </div>
                      ))
                    ) : (
                      <div style={{ ...s.noData, marginBottom: 8 }}>No suggestions loaded yet</div>
                    )}

                  </div>

                  {/* Expenses */}
                  <div style={s.blockFull}>
                    <div style={s.sectionLabel}>Expenses {divider}</div>
                    <div style={{ marginBottom: 12 }}>
                      <div style={s.expTotalLabel}>Total today</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                        <div style={s.expTotal}>¥{dayTotal.toLocaleString()}</div>
                        {dayTotalAUD !== null && (
                          <div style={{ fontFamily: 'Georgia, serif', fontSize: '1rem', color: c.textDim }}>
                            ≈ A${dayTotalAUD.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Existing expenses */}
                    {dayExpenses.items.map(item => (
                      <div key={item.id} style={s.expEntry}>
                        <span style={s.expCatLabel}>{item.category}</span>
                        <span style={s.expDesc}>{item.desc || '—'}</span>
                        <span style={s.expAmt}>¥{parseFloat(item.amount || 0).toLocaleString()}</span>
                        <button style={s.expDel} onClick={() => deleteExpense(currentDay.date, item.id)}>✕</button>
                      </div>
                    ))}

                    {/* Add expense form */}
                    <div style={s.expRow}>
                      <select style={{ ...s.expInput, flex: '1.2', minWidth: 110 }} value={expCat} onChange={e => setExpCat(e.target.value)}>
                        {EXPENSE_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input
                        style={{ ...s.expInput, flex: 2, minWidth: 120 }}
                        type="text" placeholder="Description (optional)"
                        value={expDesc} onChange={e => setExpDesc(e.target.value)}
                      />
                      <input
                        style={{ ...s.expInput, flex: 1, minWidth: 90 }}
                        type="number" placeholder="¥ Amount" min="0" step="1"
                        value={expAmount} onChange={e => setExpAmount(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') addExpense(); }}
                      />
                    </div>
                    <button style={s.btnAddExp} disabled={expSaving} onClick={addExpense}>
                      {expSaving ? 'Saving...' : '+ Add Expense'}
                    </button>
                    {expStatus && <div style={s.statusOk}>{expStatus}</div>}

                    {/* ── Currency Converter ── */}
                    <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${c.border}` }}>
                      <div style={s.sectionLabel}>Currency Converter {divider}</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                        <input
                          type="number"
                          placeholder="Amount"
                          value={fxAmount}
                          onChange={e => { setFxAmount(e.target.value); setFxResult(null); }}
                          onKeyDown={e => { if (e.key === 'Enter') { handleConvert(); }}}
                          style={{ ...s.expInput, width: 110 }}
                        />
                        <select value={fxFrom} onChange={e => { setFxFrom(e.target.value); setFxRates(null); setFxResult(null); }} style={{ ...s.expInput, width: 80 }}>
                          {CURRENCIES.map(cur => <option key={cur} value={cur}>{cur}</option>)}
                        </select>
                        <span style={{ color: c.textDim, fontSize: '0.75rem' }}>→</span>
                        <select value={fxTo} onChange={e => { setFxTo(e.target.value); setFxResult(null); }} style={{ ...s.expInput, width: 80 }}>
                          {CURRENCIES.map(cur => <option key={cur} value={cur}>{cur}</option>)}
                        </select>
                        <button
                          onClick={() => { handleConvert(); }}
                          disabled={!fxAmount || fxLoading}
                          style={{ ...s.btnAddExp, width: 'auto', padding: '7px 16px', marginTop: 0, opacity: (!fxAmount || fxLoading) ? 0.5 : 1 }}
                        >
                          {fxLoading ? '…' : 'Convert'}
                        </button>
                        <button
                          onClick={() => { const tmp = fxFrom; setFxFrom(fxTo); setFxTo(tmp); setFxRates(null); setFxResult(null); }}
                          title="Swap currencies"
                          style={{ ...s.expInput, cursor: 'pointer', padding: '7px 10px', background: 'none' }}
                        >⇄</button>
                      </div>
                      {fxResult !== null && (
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 6 }}>
                          <span style={{ fontFamily: 'Georgia, serif', fontSize: '1.4rem', color: theme.accent2 }}>
                            {fxResult.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <span style={{ fontSize: '0.72rem', color: c.textDim }}>{fxTo}</span>
                          {fxRates && <span style={{ fontSize: '0.65rem', color: c.textFaint, marginLeft: 'auto' }}>
                            1 {fxFrom} = {(fxRates.rates[fxTo] / (fxRates.base === fxFrom ? 1 : fxRates.rates[fxFrom])).toFixed(4)} {fxTo}
                          </span>}
                        </div>
                      )}
                      {fxRates && <div style={{ fontSize: '0.6rem', color: c.textFaint, marginTop: 4 }}>Live rate · open.er-api.com</div>}
                    </div>
                  </div>

                </div>{/* end grid */}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}